import { FC, useEffect, useState } from 'react';
import { useCoreContext } from 'contexts/coreContext';
import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import {
  CategoryNode,
  findNode,
  collectNodes,
} from 'components/TreeSelect/treeHelper';
import TreeSelect from 'components/TreeSelect/TreeSelect';

interface SegmentsSelectionProps {
  value: Ioption[];
  opposite: Ioption[]; // 需要 disabled 的選項
  op: 'inc' | 'exc';
  defaultTypes: string[]; // 需要預先打 API 撈資料的
  onChange: (v) => void;
}

interface Ioption {
  id: string;
  value: string;
  label: string;
}

const defaultAdRequestSourceManager = new DefaultAdRequestSourceManager();

export const SegmentsSelection: FC<SegmentsSelectionProps> = ({
  value = [],
  opposite = [],
  onChange,
  op,
  defaultTypes,
}) => {
  const core = useCoreContext();

  const defaultTreeData: CategoryNode[] = [
    // {
    //   id: 'device',
    //   value: 'device',
    //   label: '裝置類型',
    // },
    // {
    //   id: 'spaceType',
    //   value: 'spaceType',
    //   label: '版位類型',

    // },
    {
      id: 'momoSegment',
      value: 'momoSegment',
      label: 'momo 受眾',
      checkable: false,
    },
  ];

  const [treeData, setTreeData] = useState<CategoryNode[]>(
    defaultTreeData.filter(
      (item) =>
        (item.value &&
          core?.accountManager?.localeMeta?.addonFeatures?.campaignLimitation?.[
            item.value
          ]) ||
        item.value === 'momoSegment',
    ),
  );
  const [rawTreeData, setRawTreeData] =
    useState<CategoryNode[]>(defaultTreeData);
  const [loading, setLoading] = useState(false);
  const handleChange = (allSelectedIds: string[]) => {
    const leafNodes = allSelectedIds
      .map((id) => findNode(treeData, id).node)
      .filter(
        (node) => node && (!node.children || node.children.length === 0),
      ) as CategoryNode[];

    const selectedLeafNodes = leafNodes.map((item) => ({
      op,
      type: item.value ? item.value.split('___')[0] : '',
      value: item.value,
      label: item.label,
      id: item.id,
    }));

    onChange(selectedLeafNodes); // 回傳選中的葉節點
  };

  // 動態載入子節點
  const onLoadData = async (
    node: CategoryNode | null,
  ): Promise<CategoryNode[]> => {
    if (!node) return treeData;
    switch (node.value) {
      case 'device': {
        const device = await defaultAdRequestSourceManager.getDevice();
        return (
          device?.map((item) => ({
            id: 'device___' + item.value.toString(),
            value: 'device___' + item.value.toString(),
            label: item.label,
          })) || []
        );
      }
      case 'spaceType': {
        const spaceType = await defaultAdRequestSourceManager.getSpaceTypes();
        return (
          spaceType?.map((item) => ({
            id: 'spaceType___' + item.value.toString(),
            value: 'spaceType___' + item.value.toString(),
            label: item.label,
          })) || []
        );
      }
      case 'momoSegment': {
        const momoSegment = await defaultAdRequestSourceManager.getMomoSegments(
          core?.accountManager?.localeMeta?.vendorNumber || '',
        );
        let momoSegmentNodes: CategoryNode[] = [];
        momoSegment?.forEach((item) => {
          const children =
            item.options?.map((option) => ({
              id: 'momoSegment___' + option.value.toString(),
              value: 'momoSegment___' + option.value.toString(),
              label: option.label,
              checkable: true,
            })) || [];
          momoSegmentNodes.push({
            id: item.value.toString(),
            value: item.value.toString(),
            label: item.label,
            children,
            checkable: false, // 如果沒有子節點，則不可選
          });
        });
        return momoSegmentNodes;
      }
      default:
        return [];
    }
  };

  // 初始化載入資料
  useEffect(() => {
    (async () => {
      setLoading(true);
      let newTreeData = [...defaultTreeData];
      for (const node of newTreeData) {
        const children = await onLoadData(node);
        node.children = children;
      }
      setRawTreeData(newTreeData);
      setLoading(false);
    })();
  }, []);

  // 樹狀資料處理，將 opposite 的選項設為 disabled
  useEffect(() => {
    const oppositeSet = new Set(opposite.map((item) => item.value));

    const markDisabledNodes = (nodes: CategoryNode[]): CategoryNode[] =>
      nodes.map((node) => {
        const leafIds = collectNodes([node], 'leaf', 'id') as string[];
        const isDisabled =
          (op === 'exc' && leafIds.every((id) => oppositeSet.has(id))) || // 如果是排除操作，則禁用所有在 opposite 中的選項
          (op === 'inc' && leafIds.some((id) => oppositeSet.has(id))); // 指定區域的邏輯
        return {
          ...node,
          disabled: isDisabled,
          children: node.children
            ? markDisabledNodes(node.children)
            : undefined,
        };
      });

    setTreeData(markDisabledNodes(rawTreeData));
  }, [rawTreeData, opposite, op]);

  return (
    <TreeSelect
      treeData={treeData}
      value={value.map((item) => item.id)}
      onChange={handleChange}
      placeholder={`請選擇 ${op === 'inc' ? '指定受眾' : '排除指定受眾'}`}
      loading={loading}
      allowClear
      disabled={loading}
      autoCheckChildren={true}
      showSelectAll={false}
    />
  );
};
